import {
    AnalyticsUserEvent,
    getCategoryFromDisplayName,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Pressable } from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    FullScreenSpinner,
    H5Regular,
    Icon,
    type IconType,
    Image,
    PlusIcon,
    SafeAreaView,
    ScrollView,
    Sheet,
    tokens,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    BillsTotal,
    BillsTotalView,
} from '../../../bills/components/common/BillsTotal';
import useGetBillDiscoveryReview from '../../../bills/hooks/useGetBillDiscoveryReview';
import { useGetAllBills } from '../../../bills/hooks/useGetBills';
import {
    AccountImportStatus,
    useGetMembershipAccountImportStatuses,
} from '../../../bills/hooks/useGetMembershipAccountImportStatuses';
import useSubmitBillDiscoveryReview, {
    type SubmitBillDiscoveryReviewBody,
} from '../../../bills/hooks/useSubmitBillDiscoveryReview';
import {
    addBillCategoryAtom,
    billReviewSourceAtom,
    billsToReviewAtom,
    selectedReviewBillAtom,
    selectedReviewBillsAtom,
} from '../../../bills/state/BillsAtoms';
import {
    BillActivationStatus,
    BillCategory,
    BillCategoryNames,
    BillCreationType,
    type IBill,
    type PartialBill,
    type SelectableBill,
} from '../../../bills/types/Bill';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { formatDollars } from '../../../common/utils/bankingUtils';
import {
    convertCategoryToIcon,
    getBillTitle,
    getCategoryDisplayString,
    sortCategories,
} from '../../../common/utils/billUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import useConnectQuilttInstitution from '../../../holistic/hooks/useConnectQuilttInstitution';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException, track } from '../../../logger/Logger';
import { useGetLiabilities } from '../../hooks/useGetLiabilities';
import { useOnboardingCompleteness } from '../../hooks/useOnboardingCompleteness';

// Custom type to support both Lucide icons and image URLs
type IconOrImage = IconType | { uri: string } | null;

// Bill source types
type BillSource = 'SPINWHEEL' | 'QUILTT' | 'MANUAL';

// Custom SelectableBill type that supports both icons and images
interface CustomSelectableBill extends Omit<SelectableBill, 'icon'> {
    icon: IconOrImage;
    source: BillSource;
}

// Reusable Add Bill Button component
const AddBillButton = ({ onPress }: { onPress: () => void }) => {
    return (
        <Pressable
            onPress={onPress}
            style={{
                backgroundColor: tokens.color.light.orange.orange300,
                borderRadius: 20,
                paddingHorizontal: 16,
                paddingVertical: 8,
                cursor: 'pointer',
            }}
        >
            <BodyMediumRegular color={tokens.color.light.orange.orange1100}>
                + Add bill
            </BodyMediumRegular>
        </Pressable>
    );
};

const SpinwheelBillsReview = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('SpinwheelBillsReview');

    const user = useAuthedUser();

    const [isAddBillsSheetOpen, setIsAddBillsSheetOpen] = useState(false);

    const setAddBillCategory = useSetAtom(addBillCategoryAtom);

    const storeBillsToReview = useSetAtom(billsToReviewAtom);
    const [selectedReviewBills, storeSelectedReviewBills] = useAtom(
        selectedReviewBillsAtom,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const storeSelectedBill = useSetAtom(selectedReviewBillAtom);
    const toast = useToastController();
    const queryClient = useQueryClient();
    const cashFlowHomeRoute = '/';

    const { connectInstitution } = useConnectQuilttInstitution('onboarding');

    // Separate state for checkbox selections - starts empty (all unchecked)
    const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(
        new Set(),
    );
    const [liabilityBills, setLiabilityBills] = useState<
        CustomSelectableBill[]
    >([]);
    const [regularBills, setRegularBills] = useState<CustomSelectableBill[]>(
        [],
    );
    const [selectedBillCategories, setSelectedBillCategories] = useState<
        BillCategory[]
    >([]);
    const [billReviewSource] = useAtom(billReviewSourceAtom);

    // Group by institution and take first account from each institution
    const { isBillsComplete } = useOnboardingCompleteness();

    useEffect(() => {
        if (selectedReviewBills.length === 0) {
            setSelectedBillIds(new Set());
            return;
        }

        setSelectedBillIds((prev) => {
            const next = new Set(prev);
            let didChange = false;

            const selectedBills = new Set(
                selectedReviewBills
                    .filter((bill) => bill.isSelected)
                    .map((bill) => bill.id),
            );

            selectedBills.forEach((id) => {
                if (!next.has(id)) {
                    next.add(id);
                    didChange = true;
                }
            });

            Array.from(next).forEach((id) => {
                if (!selectedBills.has(id)) {
                    next.delete(id);
                    didChange = true;
                }
            });

            return didChange ? next : prev;
        });
    }, [selectedReviewBills]);

    const { data: liabilitiesData, refetch: refetchLiabilities } =
        useGetLiabilities();

    // Get actual bills data
    const {
        allBills: billsData,
        refetchBills,
        isLoadingAllBills,
    } = useGetAllBills();

    const {
        refetch: refetchBillDiscoveryReview,
        isLoading: isLoadingBillDiscoveryReview,
    } = useGetBillDiscoveryReview(user.membership?.id ?? '');

    const [shouldPollForPendingJobs, setShouldPollForPendingJobs] =
        useState(true);
    const [billsStillProcessing, setBillsStillProcessing] = useState(true);

    const { membershipAccountImportStatuses } =
        useGetMembershipAccountImportStatuses(
            user.membership?.id ?? '',
            shouldPollForPendingJobs,
        );

    const refetchEverything = async () => {
        // Invalidate and refetch bills queries
        await queryClient.invalidateQueries({
            queryKey: [
                `getBills/${user?.membership?.id ?? ''}`,
                `getBills/${user?.membership?.id ?? ''}/ACTIVE`,
                `getBills/${user?.membership?.id ?? ''}/undefined`,
                `useGetBillDiscoveryReview/${user?.membership?.id ?? ''}`,
                'getPendingJobs',
                'getLiabilities',
            ],
        });

        // the cash flow overview page is using both the discovery review hook AND the allBills atom that the regular bills hook feeds
        // TODO: clean up the overview page to use a single hook/atom to consume bills data from a single source

        // we need refetchBillDiscoveryReview to happen first so the bills table in postgres can be loaded from ai service
        await refetchBillDiscoveryReview();
        refetchBills();
        void refetchLiabilities();
    };

    useEffect(() => {
        const accountsThatAreStillProcessing = (
            membershipAccountImportStatuses?.accountStatusesAndCounts ?? []
        ).filter((accountImportStatus) => {
            return (
                [
                    AccountImportStatus.PENDING,
                    AccountImportStatus.UNIMPORTED,
                ].includes(accountImportStatus.status) ||
                (!accountImportStatus.hasRunBillsJobAfterImport &&
                    accountImportStatus.status !== AccountImportStatus.ERROR)
            );
        });

        LogRocketFacade.log(
            `Accounts still processing: ${JSON.stringify(accountsThatAreStillProcessing)}`,
        );

        const stillProcessing =
            accountsThatAreStillProcessing.length !== 0 ||
            (membershipAccountImportStatuses?.successfulBillJobData
                ?.hasPendingBillJob ??
                false);

        setBillsStillProcessing(stillProcessing);

        if (!stillProcessing) {
            void refetchEverything();
        }
        setShouldPollForPendingJobs(stillProcessing);
    }, [membershipAccountImportStatuses]);

    // Transform bills data for display
    useEffect(() => {
        if (
            liabilitiesData?.liabilities != null &&
            billsData != null &&
            (liabilityBills.length === 0 || regularBills.length === 0)
        ) {
            const newLiabilityBills: CustomSelectableBill[] = [];
            const newRegularBills: CustomSelectableBill[] = [];

            // All bills now come from the bills endpoint (including Spinwheel CCs)
            billsData.forEach((bill) => {
                // Determine the source based on creation type
                const source: BillSource =
                    bill.creationType === BillCreationType.MANUAL ||
                    bill.creationType === BillCreationType.MONEY_MAPPER
                        ? 'MANUAL'
                        : bill.creationType === BillCreationType.SPINWHEEL
                          ? 'SPINWHEEL'
                          : 'QUILTT';

                // Default to selected if activationStatus is 'ACTIVE'
                const isSelected =
                    bill.activationStatus === BillActivationStatus.ACTIVE;

                const transformedBill = {
                    ...bill,
                    isSelected,
                    source,
                };

                // Separate CC minimum bills from regular bills
                if (bill.category === BillCategory.CREDIT_CARD_MINIMUM) {
                    newLiabilityBills.push(transformedBill);
                } else {
                    newRegularBills.push(transformedBill);
                }
            });

            const allBills = [...newLiabilityBills, ...newRegularBills];

            setLiabilityBills(newLiabilityBills);
            setRegularBills(newRegularBills);

            storeBillsToReview(allBills as SelectableBill[]);
            storeSelectedReviewBills((prevBills) => {
                const prevMap = new Map(
                    prevBills.map((bill) => [bill.id, bill]),
                );
                return (allBills as SelectableBill[]).map((bill) => {
                    const previous = prevMap.get(bill.id);
                    if (previous) {
                        return {
                            ...bill,
                            isSelected: previous.isSelected,
                        };
                    }

                    return {
                        ...bill,
                        isSelected:
                            bill.activationStatus ===
                            BillActivationStatus.ACTIVE,
                    };
                });
            });
        }
    }, [liabilitiesData, billsData]);

    // Group bills by category
    const billsByCategory = useMemo(() => {
        const grouped: Record<string, CustomSelectableBill[] | BillCategory[]> =
            {};

        // Group liability bills under "Credit card minimums"
        if (liabilityBills.length > 0) {
            grouped['Credit card minimums'] = liabilityBills;
        }

        // Group regular bills by category
        regularBills.forEach((bill) => {
            const categoryName = getCategoryDisplayString(bill.category);
            grouped[categoryName] ??= [];
            (grouped[categoryName] as CustomSelectableBill[]).push(bill);
        });

        // Check for missing essential bill categories
        const essentialCategories = [
            BillCategory.RENT_MORTGAGE,
            BillCategory.UTILITIES,
            BillCategory.INSURANCE,
        ];

        const missingCategories = essentialCategories.filter(
            (category) => !grouped[getCategoryDisplayString(category)],
        );

        // Add missing bills section if any essential categories are missing and processing is complete
        if (missingCategories.length > 0 && !billsStillProcessing) {
            grouped['Missing bills'] = missingCategories;
        }

        return grouped;
    }, [liabilityBills, regularBills, billsStillProcessing]);

    // Get unique categories from regular bills
    const allBillCategories = useMemo(() => {
        const regularCategories = Array.from(
            new Set(regularBills.map((bill) => bill.category)),
        );
        // Add CC min payments if we have any liability bills
        const categories =
            liabilityBills.length > 0
                ? [BillCategory.CREDIT_CARD_MINIMUM, ...regularCategories]
                : regularCategories;

        return categories.sort(sortCategories);
    }, [regularBills, liabilityBills]);

    // Filter bills based on selected categories
    const filteredBillsByCategory = useMemo(() => {
        const selectedSet = new Set<BillCategory>(selectedBillCategories);

        const entries = Object.entries(billsByCategory).flatMap(
            ([categoryName, bills]) => {
                if (selectedSet.size === 0)
                    return [[categoryName, bills] as const];

                if (categoryName === 'Credit card minimums') {
                    return selectedSet.has(BillCategory.CREDIT_CARD_MINIMUM)
                        ? [[categoryName, bills] as const]
                        : [];
                }

                const kept = (bills as CustomSelectableBill[]).filter((b) =>
                    selectedSet.has(b.category),
                );

                return kept.length ? [[categoryName, kept] as const] : [];
            },
        );

        const nameToCategory = (name: string): BillCategory | null =>
            name === 'Credit card minimums'
                ? BillCategory.CREDIT_CARD_MINIMUM
                : getCategoryFromDisplayName(name);

        entries.sort(([a], [b]) => {
            const ca = nameToCategory(a);
            const cb = nameToCategory(b);
            return ca != null && cb != null ? sortCategories(ca, cb) : 0;
        });

        return Object.fromEntries(entries) as Record<
            string,
            CustomSelectableBill[] | BillCategory[]
        >;
    }, [billsByCategory, selectedBillCategories]);

    // No longer needed - bills are stored with correct isSelected value in the transform useEffect above

    const toggleSelection = (bill: CustomSelectableBill) => {
        setSelectedBillIds((prev) => {
            const next = new Set(prev);
            if (next.has(bill.id)) {
                next.delete(bill.id);
            } else {
                next.add(bill.id);
            }
            return next;
        });

        storeSelectedReviewBills((prevBills) =>
            prevBills.map((existingBill) =>
                existingBill.id === bill.id
                    ? {
                          ...existingBill,
                          isSelected: !existingBill.isSelected,
                      }
                    : existingBill,
            ),
        );
    };

    const editBill = (bill: CustomSelectableBill) => {
        // All bills (including CC minimums) now go to the same edit page
        // since they're all stored as bills in the database
        const billForEditing: IBill = {
            id: bill.id,
            aiBillId: bill.aiBillId ?? '',
            membershipId: bill.membershipId ?? user?.membership?.id ?? '',
            accountId: bill.accountId ?? '',
            name: bill.name,
            displayName:
                bill.displayName !== 'unknown'
                    ? (bill.displayName ?? bill.name)
                    : bill.name,
            payee:
                bill.payee !== 'unknown'
                    ? (bill.payee ?? bill.name)
                    : bill.name,
            category: bill.category,
            description: bill.description ?? '',
            amount: bill.amount,
            monthlyAmount: bill.monthlyAmount,
            cadence: bill.cadence,
            timingLabel: bill.timingLabel ?? '',
            timingDescription: bill.timingDescription ?? '',
            creationType: bill.creationType,
            createdAt: bill.createdAt,
            activationStatus: bill.activationStatus,
            removedAt: bill.removedAt,
            transactions: bill.transactions ?? [],
            proposedChanges: bill.proposedChanges,
            lastPaidAt: bill.lastPaidAt,
            memberMarkedAsSwitched: bill.memberMarkedAsSwitched ?? null,
            icon:
                typeof bill.icon === 'object' &&
                bill.icon !== null &&
                'uri' in bill.icon
                    ? null
                    : bill.icon,
            color: bill.color,
        };

        storeSelectedBill(billForEditing);
        router.navigate('/onboarding/expenses/bill-review-edit');
    };

    const { mutateAsync: submitReviewAsync } = useSubmitBillDiscoveryReview();

    const onSubmit = async () => {
        const acceptedBills = (
            selectedReviewBills as CustomSelectableBill[]
        ).filter((b) => selectedBillIds.has(b.id));
        const rejectedBills = (
            selectedReviewBills as CustomSelectableBill[]
        ).filter((b) => !selectedBillIds.has(b.id));

        track(
            AnalyticsUserEvent.ONBOARDING_FUNNEL_BILLS_CONFIRMED,
            {
                acceptedBillsCount: acceptedBills.length,
            },
            true,
        );
        setIsSubmitting(true);

        // All bills (including CC minimums) are now handled by the same endpoint
        const acceptedBillIds = acceptedBills
            .map((bill) => bill.id)
            .filter((id) => id !== '');
        const rejectedBillIds = rejectedBills
            .map((bill) => bill.id)
            .filter((id) => id !== '');

        const reviewedAccountIds = Array.from(
            new Set(selectedReviewBills.map((bill) => bill.accountId)),
        );

        try {
            // Submit bills review
            await submitReviewAsync({
                urlParams: {
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    acceptedBillIds,
                    rejectedBillIds,
                    reviewedAccountIds,
                } satisfies SubmitBillDiscoveryReviewBody,
            });

            await refetchEverything();

            if (billReviewSource === 'profile') {
                router.navigate('/settings');
            } else if (billReviewSource === 'magic-numbers-onboarding') {
                track(
                    AnalyticsUserEvent.ONBOARDING_FUNNEL_BILLS_CONFIRMED,
                    undefined,
                    true,
                );
                router.push('/home');
            } else {
                track(
                    AnalyticsUserEvent.ONBOARDING_FUNNEL_BILLS_CONFIRMED,
                    undefined,
                    true,
                );
                router.navigate(cashFlowHomeRoute);
            }

            setIsSubmitting(false);
        } catch (error) {
            toast.show('Something went wrong');
            captureException(error);
            setIsSubmitting(false);
        }
    };

    const handleAddBill = (category?: BillCategory) => {
        setIsAddBillsSheetOpen(true);
        setAddBillCategory(category ?? null);
    };

    const handleConnectMoreAccounts = () => {
        setIsAddBillsSheetOpen(false);
        connectInstitution();
    };

    const handleAddBillsManually = () => {
        setIsAddBillsSheetOpen(false);
        router.navigate('/bills/bill-review-add-new');
    };

    const handleGoBack = () => {
        if (billReviewSource === 'profile') {
            router.navigate('/settings');
        } else if (
            billReviewSource === 'home-review' ||
            billReviewSource === 'home'
        ) {
            // Coming from BeforeMoneyMapSession or from home - always go to home
            router.navigate('/home');
        } else {
            router.navigate(
                billReviewSource === 'magic-numbers-onboarding' ||
                    isBillsComplete
                    ? '/home'
                    : '/onboarding/connect-accounts',
            );
        }
    };

    if (isSubmitting || isLoadingAllBills || isLoadingBillDiscoveryReview) {
        return <FullScreenSpinner />;
    }

    const allBills = Object.values(filteredBillsByCategory)
        .filter(
            (bills): bills is CustomSelectableBill[] =>
                Array.isArray(bills) &&
                bills.length > 0 &&
                typeof bills[0] === 'object' &&
                bills[0] !== null &&
                'monthlyAmount' in bills[0],
        )
        .flat() as IBill[];

    const allSelectedBills = Object.values(filteredBillsByCategory)
        .filter(
            (bills): bills is CustomSelectableBill[] =>
                Array.isArray(bills) &&
                bills.length > 0 &&
                typeof bills[0] === 'object' &&
                bills[0] !== null &&
                'monthlyAmount' in bills[0],
        )
        .flat()
        .filter((bill) => selectedBillIds.has(bill.id)) as IBill[];

    return (
        <SafeAreaView
            style={{
                flex: 1,
                backgroundColor: tokens.color.light.brand.white,
                maxWidth: 700,
                alignSelf: 'center',
            }}
        >
            <YStack flex={1}>
                {/* Header */}
                <XStack
                    paddingHorizontal={24}
                    paddingTop={16}
                    paddingBottom={16}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <BackButton
                        fallbackRoute="/home"
                        customBackFn={handleGoBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                    <XStack width={40} />
                </XStack>

                {/* Content - Full Screen Scrollable */}
                <ScrollView
                    flex={1}
                    showsVerticalScrollIndicator={false}
                    stickyHeaderIndices={[1]}
                >
                    <YStack
                        paddingHorizontal={24}
                        paddingTop={16}
                        paddingBottom={0}
                    >
                        <YStack gap={12} marginBottom={12}>
                            <H5Regular color="$black">
                                Here&apos;s what we found
                            </H5Regular>
                            <BodyMediumRegular color="$gray1000">
                                Review and check off all bills that look
                                accurate. You can also edit the details, or add
                                any missing bills manually.
                            </BodyMediumRegular>
                        </YStack>
                    </YStack>

                    {/* Sticky Section - Summary + Pills */}
                    <YStack
                        paddingHorizontal={24}
                        paddingBottom={2}
                        paddingTop={8}
                        backgroundColor="$white"
                        borderBottomWidth={1}
                        borderBottomColor="$gray200"
                    >
                        {/* Summary */}
                        <YStack marginBottom={16}>
                            <BillsTotal
                                bills={allBills}
                                selectedBills={allSelectedBills}
                                viewType={BillsTotalView.DiscoveryReview}
                                showSelectionCount={true}
                            />
                        </YStack>

                        {/* Filter Pills */}
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            marginHorizontal={-24}
                            style={{ minHeight: 60 }}
                        >
                            <XStack gap={12} paddingHorizontal={24}>
                                <Button
                                    variant={
                                        selectedBillCategories.length === 0
                                            ? ButtonVariant.PRIMARY
                                            : ButtonVariant.SECONDARY
                                    }
                                    size={ButtonSize.SMALL}
                                    width="auto"
                                    text="All"
                                    icon={convertCategoryToIcon('all')}
                                    onPress={() => {
                                        setSelectedBillCategories([]);
                                    }}
                                    textProps={{ fontWeight: '400' }}
                                />
                                {allBillCategories.map((billCategory) => (
                                    <Button
                                        key={billCategory}
                                        variant={
                                            selectedBillCategories.includes(
                                                billCategory,
                                            )
                                                ? ButtonVariant.PRIMARY
                                                : ButtonVariant.SECONDARY
                                        }
                                        size={ButtonSize.SMALL}
                                        width="auto"
                                        text={BillCategoryNames[billCategory]}
                                        icon={convertCategoryToIcon(
                                            billCategory,
                                        )}
                                        onPress={() => {
                                            if (
                                                selectedBillCategories.includes(
                                                    billCategory,
                                                )
                                            ) {
                                                setSelectedBillCategories([]);
                                            } else {
                                                setSelectedBillCategories([
                                                    billCategory,
                                                ]);
                                            }
                                        }}
                                        textProps={{ fontWeight: '400' }}
                                    />
                                ))}
                            </XStack>
                        </ScrollView>
                    </YStack>

                    <YStack
                        paddingHorizontal={24}
                        paddingTop={16}
                        paddingBottom={100}
                    >
                        {/* Bills List */}
                        <YStack gap={16}>
                            {Object.entries(filteredBillsByCategory).map(
                                ([categoryName, bills]) => {
                                    return (
                                        <YStack
                                            key={categoryName}
                                            backgroundColor="$gray0"
                                            borderRadius={12}
                                            marginBottom={16}
                                            overflow="hidden"
                                        >
                                            {/* Category Header - Darker Grey */}
                                            <XStack
                                                alignItems="center"
                                                paddingHorizontal={16}
                                                paddingVertical={8}
                                                backgroundColor="$gray200"
                                                gap={12}
                                            >
                                                {categoryName ===
                                                'Missing bills' ? null : (
                                                    <View
                                                        width={40}
                                                        height={40}
                                                        alignItems="center"
                                                        justifyContent="center"
                                                    >
                                                        <Icon
                                                            icon={
                                                                categoryName ===
                                                                'Credit card minimums'
                                                                    ? convertCategoryToIcon(
                                                                          BillCategory.CREDIT_CARD_MINIMUM,
                                                                      )
                                                                    : convertCategoryToIcon(
                                                                          getCategoryFromDisplayName(
                                                                              categoryName,
                                                                          ) ??
                                                                              undefined,
                                                                      )
                                                            }
                                                            size={20}
                                                            color="$black"
                                                        />
                                                    </View>
                                                )}
                                                <BodyMediumRegular>
                                                    {categoryName}
                                                </BodyMediumRegular>
                                            </XStack>

                                            {/* Missing Bills Content */}
                                            {categoryName ===
                                            'Missing bills' ? (
                                                <YStack
                                                    paddingHorizontal={16}
                                                    paddingVertical={16}
                                                >
                                                    <BodyMediumRegular
                                                        color="$gray1000"
                                                        marginBottom={16}
                                                    >
                                                        We noticed you&apos;re
                                                        missing bills in some
                                                        common categories. Add
                                                        any missing bills to
                                                        give your Guide a more
                                                        complete view of your
                                                        monthly cash flow.
                                                    </BodyMediumRegular>
                                                    <YStack gap={12}>
                                                        {(
                                                            bills as BillCategory[]
                                                        ).map((category) => {
                                                            const categoryDisplayName =
                                                                getCategoryDisplayString(
                                                                    category,
                                                                );
                                                            const icon =
                                                                convertCategoryToIcon(
                                                                    category,
                                                                );

                                                            return (
                                                                <XStack
                                                                    key={
                                                                        category
                                                                    }
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                    paddingVertical={
                                                                        12
                                                                    }
                                                                    paddingHorizontal={
                                                                        16
                                                                    }
                                                                    backgroundColor="$white"
                                                                    borderRadius={
                                                                        8
                                                                    }
                                                                >
                                                                    <XStack
                                                                        alignItems="center"
                                                                        gap={12}
                                                                    >
                                                                        <View
                                                                            width={
                                                                                40
                                                                            }
                                                                            height={
                                                                                40
                                                                            }
                                                                            borderRadius={
                                                                                8
                                                                            }
                                                                            backgroundColor="$gray200"
                                                                            alignItems="center"
                                                                            justifyContent="center"
                                                                        >
                                                                            <Icon
                                                                                icon={
                                                                                    icon
                                                                                }
                                                                                size={
                                                                                    24
                                                                                }
                                                                                color="$black"
                                                                            />
                                                                        </View>
                                                                        <BodyMediumRegular>
                                                                            {
                                                                                categoryDisplayName
                                                                            }
                                                                        </BodyMediumRegular>
                                                                    </XStack>
                                                                    <AddBillButton
                                                                        onPress={() => {
                                                                            handleAddBill(
                                                                                category,
                                                                            );
                                                                        }}
                                                                    />
                                                                </XStack>
                                                            );
                                                        })}
                                                    </YStack>
                                                </YStack>
                                            ) : (
                                                /* Regular Bills Content */
                                                (
                                                    bills as CustomSelectableBill[]
                                                )
                                                    .sort(
                                                        (a, b) =>
                                                            b.monthlyAmount -
                                                            a.monthlyAmount,
                                                    )
                                                    .map((bill) => (
                                                        <YStack
                                                            key={bill.id}
                                                            paddingVertical={12}
                                                            paddingHorizontal={
                                                                16
                                                            }
                                                        >
                                                            <XStack
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                            >
                                                                {/* Bill content - clickable for editing */}
                                                                <Pressable
                                                                    onPress={() => {
                                                                        editBill(
                                                                            bill,
                                                                        );
                                                                    }}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    <XStack
                                                                        alignItems="center"
                                                                        justifyContent="space-between"
                                                                    >
                                                                        {/* Left side - Icon and Bill info */}
                                                                        <XStack
                                                                            alignItems="center"
                                                                            gap={
                                                                                12
                                                                            }
                                                                            flex={
                                                                                1
                                                                            }
                                                                        >
                                                                            <View
                                                                                width={
                                                                                    40
                                                                                }
                                                                                height={
                                                                                    40
                                                                                }
                                                                                borderRadius={
                                                                                    8
                                                                                }
                                                                                backgroundColor={
                                                                                    (bill.color as string) ??
                                                                                    '$gray200'
                                                                                }
                                                                                alignItems="center"
                                                                                justifyContent="center"
                                                                            >
                                                                                {bill.icon &&
                                                                                    (typeof bill.icon ===
                                                                                        'object' &&
                                                                                    bill.icon !==
                                                                                        null &&
                                                                                    'uri' in
                                                                                        bill.icon ? (
                                                                                        <Image
                                                                                            source={
                                                                                                bill.icon as {
                                                                                                    uri: string;
                                                                                                }
                                                                                            }
                                                                                            width={
                                                                                                20
                                                                                            }
                                                                                            height={
                                                                                                20
                                                                                            }
                                                                                            resizeMode="contain"
                                                                                        />
                                                                                    ) : (
                                                                                        <Icon
                                                                                            icon={
                                                                                                bill.icon
                                                                                            }
                                                                                            size={
                                                                                                20
                                                                                            }
                                                                                            color="$white"
                                                                                        />
                                                                                    ))}
                                                                            </View>
                                                                            <YStack
                                                                                flex={
                                                                                    1
                                                                                }
                                                                            >
                                                                                <BodyMediumRegular
                                                                                    numberOfLines={
                                                                                        1
                                                                                    }
                                                                                >
                                                                                    {getBillTitle(
                                                                                        bill as PartialBill,
                                                                                    )}
                                                                                </BodyMediumRegular>
                                                                                <XStack
                                                                                    alignItems="center"
                                                                                    justifyContent="space-between"
                                                                                    width="100%"
                                                                                >
                                                                                    <BodySmallRegular>
                                                                                        {formatDollars(
                                                                                            bill.monthlyAmount,
                                                                                        )}
                                                                                        {
                                                                                            ' monthly'
                                                                                        }
                                                                                    </BodySmallRegular>
                                                                                </XStack>
                                                                            </YStack>
                                                                        </XStack>
                                                                    </XStack>
                                                                </Pressable>

                                                                {/* Checkbox - separate from bill content */}
                                                                <XStack
                                                                    marginLeft={
                                                                        16
                                                                    }
                                                                >
                                                                    <Pressable
                                                                        onPress={() => {
                                                                            toggleSelection(
                                                                                bill,
                                                                            );
                                                                        }}
                                                                        hitSlop={
                                                                            10
                                                                        }
                                                                    >
                                                                        <View
                                                                            width={
                                                                                22
                                                                            }
                                                                            height={
                                                                                22
                                                                            }
                                                                            borderRadius={
                                                                                6
                                                                            }
                                                                            borderWidth={
                                                                                2
                                                                            }
                                                                            borderColor={
                                                                                selectedBillIds.has(
                                                                                    bill.id,
                                                                                )
                                                                                    ? '$accent900'
                                                                                    : '$gray1200'
                                                                            }
                                                                            backgroundColor={
                                                                                selectedBillIds.has(
                                                                                    bill.id,
                                                                                )
                                                                                    ? '$accent900'
                                                                                    : '$white'
                                                                            }
                                                                            alignItems="center"
                                                                            justifyContent="center"
                                                                        >
                                                                            {selectedBillIds.has(
                                                                                bill.id,
                                                                            ) && (
                                                                                <Icon
                                                                                    icon={
                                                                                        CheckIcon
                                                                                    }
                                                                                    size={
                                                                                        12
                                                                                    }
                                                                                    color="$white"
                                                                                />
                                                                            )}
                                                                        </View>
                                                                    </Pressable>
                                                                </XStack>
                                                            </XStack>
                                                        </YStack>
                                                    ))
                                            )}
                                        </YStack>
                                    );
                                },
                            )}
                        </YStack>

                        <YStack paddingVertical={24} width="100%" gap={16}>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                icon={PlusIcon}
                                text="Add a bill"
                                onPress={() => {
                                    handleAddBill();
                                }}
                                size={ButtonSize.LARGE}
                            />
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Confirm bills"
                                onPress={onSubmit}
                                disabled={allSelectedBills.length === 0}
                            />
                        </YStack>
                    </YStack>
                </ScrollView>
            </YStack>

            {/* Add Missing Bills Sheet */}
            <Sheet
                isOpen={isAddBillsSheetOpen}
                title="Want to see more bills?"
                showCloseButton={true}
                onClose={() => {
                    setIsAddBillsSheetOpen(false);
                }}
            >
                <YStack width="100%" paddingTop={16} gap={24}>
                    <BodyMediumRegular color="$gray1000">
                        Connect additional accounts for us to scan, or add any
                        missing bills manually.
                    </BodyMediumRegular>
                    <YStack gap={8}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Connect accounts"
                            onPress={handleConnectMoreAccounts}
                        />
                        <Button
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.LARGE}
                            text="Add a bill"
                            onPress={handleAddBillsManually}
                        />
                    </YStack>
                </YStack>
            </Sheet>
        </SafeAreaView>
    );
};

export default SpinwheelBillsReview;
